import { closeBrackets, deleteBracketPair } from "@codemirror/autocomplete";
import { indentUnit } from "@codemirror/language";
import { Compartment, EditorState, Extension } from "@codemirror/state";
import { EditorView, highlightSpecialChars, highlightTrailingWhitespace, highlightWhitespace, keymap, lineNumbers } from "@codemirror/view";
import { isProductionHost } from "../common/util";
import { setBarVisible } from "./shortcutbar";
import { debugHighlightTagsTooltip } from "./views/debug";
import { insertSpacesKeymap, insertTabKeymap } from "./views/tabs";

declare var bootbox: BootboxStatic;
declare var $: JQueryStatic;

export const highlightSpecialCharsCompartment = new Compartment();
export const highlightWhitespaceCompartment = new Compartment();
export const highlightTrailingWhitespaceCompartment = new Compartment();
export const tabSizeCompartment = new Compartment();
export const closeBracketsCompartment = new Compartment();
export const tabsToSpacesCompartment = new Compartment();
export const debugHighlightTagsCompartment = new Compartment();
export const lineNumbersCompartment = new Compartment();

export interface EditorSettings {
  tabSize: number;
  tabsToSpaces: boolean;
  highlightSpecialChars: boolean;
  highlightWhitespace: boolean;
  highlightTrailingWhitespace: boolean;
  closeBrackets: boolean;
  debugHighlightTags: boolean;
  showLineNumbers: boolean;
  showStatusBar: boolean;
}

const SETTINGS_KEY = "8bitworkshop/editorSettings";

const defaultSettings: EditorSettings = {
  tabSize: 8,
  tabsToSpaces: true,
  highlightSpecialChars: false,
  highlightWhitespace: false,
  highlightTrailingWhitespace: false,
  closeBrackets: false,
  debugHighlightTags: false,
  showLineNumbers: false,
  showStatusBar: true,
};

export function loadSettings(): EditorSettings {
  try {
    var stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) };
    }
  } catch (e) { }
  return { ...defaultSettings };
}

export function saveSettings(settings: EditorSettings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

const compartmentValues: [Compartment, (s: EditorSettings) => Extension][] = [
  [tabSizeCompartment, s => [EditorState.tabSize.of(s.tabSize), indentUnit.of(" ".repeat(s.tabSize))]],
  [tabsToSpacesCompartment, s => keymap.of(s.tabsToSpaces ? insertSpacesKeymap : insertTabKeymap)],
  [highlightSpecialCharsCompartment, s => s.highlightSpecialChars ? highlightSpecialChars() : []],
  [highlightWhitespaceCompartment, s => s.highlightWhitespace ? highlightWhitespace() : []],
  [highlightTrailingWhitespaceCompartment, s => s.highlightTrailingWhitespace ? highlightTrailingWhitespace() : []],
  [closeBracketsCompartment, s => s.closeBrackets ? [closeBrackets(), keymap.of([{ key: "Backspace", run: deleteBracketPair }])] : []],
  [debugHighlightTagsCompartment, s => s.debugHighlightTags ? debugHighlightTagsTooltip : []],
  [lineNumbersCompartment, s => s.showLineNumbers ? lineNumbers() : []],
];

export function settingsExtensions(settings: EditorSettings): Extension[] {
  return compartmentValues.map(([c, fn]) => c.of(fn(settings)));
}

// Track all active editor views so we can reconfigure them
const activeEditors: Set<EditorView> = new Set();

export function registerEditor(editor: EditorView) {
  activeEditors.add(editor);
}

export function unregisterEditor(editor: EditorView) {
  activeEditors.delete(editor);
}

export function applySettingsToAll(settings: EditorSettings) {
  var effects = compartmentValues.map(([c, fn]) => c.reconfigure(fn(settings)));
  for (var editor of activeEditors) {
    editor.dispatch({ effects });
  }
}

export function openSettings() {
  var settings = loadSettings();
  var showInternal = !isProductionHost();
  bootbox.dialog({
    onEscape: true,
    title: "Settings",
    message: `<form id="settingsForm" onsubmit="return false">
       <div class="form-group"><label>Tab size: <input type="number" id="setting_tabSize" min="1" max="40" value="${settings.tabSize}" style="width:4em"></label></div>
       <div class="checkbox"><label><input type="checkbox" id="setting_tabsToSpaces" ${settings.tabsToSpaces ? 'checked' : ''}> Insert spaces when pressing TAB</label></div>
       <div class="checkbox"><label><input type="checkbox" id="setting_highlightSpecialChars" ${settings.highlightSpecialChars ? 'checked' : ''}> Highlight special characters</label></div>
       <div class="checkbox"><label><input type="checkbox" id="setting_highlightWhitespace" ${settings.highlightWhitespace ? 'checked' : ''}> Highlight whitespace</label></div>
       <div class="checkbox"><label><input type="checkbox" id="setting_highlightTrailingWhitespace" ${settings.highlightTrailingWhitespace ? 'checked' : ''}> Highlight unwanted trailing whitespace</label></div>
       <div class="checkbox"><label><input type="checkbox" id="setting_showLineNumbers" ${settings.showLineNumbers ? 'checked' : ''}> Show line numbers</label></div>
       <div class="checkbox"><label><input type="checkbox" id="setting_closeBrackets" ${settings.closeBrackets ? 'checked' : ''}> Automatically add and remove closing brackets</label></div>
       <div class="checkbox"><label><input type="checkbox" id="setting_showStatusBar" ${settings.showStatusBar ? 'checked' : ''}> Show keyboard shortcuts / status bar</label></div>
       ${showInternal ? `
       <hr>
       <h5>Internal</h5>
       <div class="checkbox"><label><input type="checkbox" id="setting_debugHighlightTags" ${settings.debugHighlightTags ? 'checked' : ''}> Debug editor syntax highlighting tags</label></div>
       ` : ''}
      </form>`,
    buttons: {
      cancel: {
        label: "Cancel",
        className: "btn-default"
      },
      ok: {
        label: "Save",
        className: "btn-primary",
        callback: () => {
          settings.tabSize = parseInt($('#setting_tabSize').val() as string) || 8;
          settings.tabsToSpaces = $('#setting_tabsToSpaces').is(':checked');
          settings.highlightSpecialChars = $('#setting_highlightSpecialChars').is(':checked');
          settings.highlightWhitespace = $('#setting_highlightWhitespace').is(':checked');
          settings.highlightTrailingWhitespace = $('#setting_highlightTrailingWhitespace').is(':checked');
          settings.closeBrackets = $('#setting_closeBrackets').is(':checked');
          settings.showLineNumbers = $('#setting_showLineNumbers').is(':checked');
          if (showInternal) settings.debugHighlightTags = $('#setting_debugHighlightTags').is(':checked');
          settings.showStatusBar = $('#setting_showStatusBar').is(':checked');
          saveSettings(settings);
          applySettingsToAll(settings);
          setBarVisible(settings.showStatusBar);
        }
      }
    }
  });
}
